import { useState } from 'react';
import clsx from 'clsx';
import { AlertCircleIcon, lineupBackground } from '@/common/assets';
import { TrackingEvents, TrackingElements, TrackingTabs, useTrack } from '../../tracking';
import { Platforms, PlayerOnPitchStats } from '@/common/constants';
import { usePlatform } from '@/common/hooks';
import { useNewTranslations } from '@/common/translations';
import type { PlayerOnPitchStatsType, Option } from '@/common/types';
import { T } from '@/common/enums';
import { Filter, FilterChipGroup, LineupSkeleton } from '@/ui/components';
import { Lineup, NoDataError, UnableToLoadError } from '../../components';
import { TeamFilterOptionValues } from '../../constants';
import { usePrematchData } from '../../hooks';
import type { TeamFilterOption } from '../../types';
import { isLineupTabEmpty } from './isLineupTabEmpty';
import SubstitutesSection from './SubstitutesSection';

// TODO: https://obgbu.atlassian.net/browse/PMAR-1384
// these are temporary names, take them from context once we start displaying real data
const HOME_TEAM_NAME = 'Nottingham Forest';
const AWAY_TEAM_NAME = 'Manchester City';

export default function LineupTab() {
  const platform = usePlatform();
  const translate = useNewTranslations();

  const [teamFilterValue, setTeamFilterValue] = useState<TeamFilterOption>(
    TeamFilterOptionValues.HOME_TEAM,
  );
  const [playerStatsFilterValue, setPlayerStatsFilterValue] =
    useState<PlayerOnPitchStatsType | null>(PlayerOnPitchStats.GOALS);
  const { teams, lineup, lineupStatus, refetchAll } = usePrematchData();
  const track = useTrack();

  const handleRetryClick = () => {
    track(TrackingEvents.CtaClick, {
      tab: TrackingTabs.Lineup,
      filter: playerStatsFilterValue ?? undefined,
      element: TrackingElements.Retry,
    });
    refetchAll();
  };

  if (lineupStatus === 'pending') {
    return (
      <LineupSkeleton
        className={clsx({
          'pt-6 px-3 pb-3': platform === Platforms.MOBILE,
          'pt-3': platform !== Platforms.MOBILE,
        })}
      />
    );
  }

  if (lineupStatus === 'error') {
    return <UnableToLoadError onRetryClick={handleRetryClick} />;
  }

  if (lineup === null || isLineupTabEmpty(lineup)) {
    return <NoDataError />;
  }

  const team = teamFilterValue === TeamFilterOptionValues.HOME_TEAM ? lineup.home : lineup.away;

  const jerseySrc =
    teamFilterValue === TeamFilterOptionValues.HOME_TEAM ? teams.home.jersey : teams.away.jersey;

  const teamFilterOptions: Option<TeamFilterOption>[] = [
    { label: HOME_TEAM_NAME, value: TeamFilterOptionValues.HOME_TEAM },
    { label: AWAY_TEAM_NAME, value: TeamFilterOptionValues.AWAY_TEAM },
  ];

  const playerStatsFilterOptions: Option<PlayerOnPitchStatsType>[] = [
    { label: translate('PreMatchStats.Goalscorer'), value: PlayerOnPitchStats.GOALS },
    { label: translate('PreMatchStats.Assists'), value: PlayerOnPitchStats.ASSISTS },
    { label: translate('PreMatchStats.Goal&Assist'), value: PlayerOnPitchStats.BOTH },
  ];

  const handleTeamFilterChange = (value: TeamFilterOption) => {
    track(TrackingEvents.FilterClick, {
      tab: TrackingTabs.Lineup,
      filter: value,
    });
    setTeamFilterValue(value);
  };

  const handlePlayerStatsFilterChange = (value: PlayerOnPitchStatsType | null) => {
    if (value !== null && value !== PlayerOnPitchStats.NONE) {
      const elementOrder =
        playerStatsFilterOptions.findIndex((option) => option.value === value) + 1;
      track(TrackingEvents.FilterClick, {
        tab: TrackingTabs.Lineup,
        filter: value,
        elementOrder,
      });
    }
    setPlayerStatsFilterValue(value);
  };

  // T.DARK forces dark mode in the nested components.
  return (
    <div
      className={clsx(
        'h-full overflow-x-hidden [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] flex flex-col gap-6 justify-between',
        T.DARK,
        platform === Platforms.DESKTOP && 'relative mt-3',
      )}
    >
      <div
        className="absolute inset-0 z-[-1]"
        style={{ background: `url(${lineupBackground}) center center / cover no-repeat` }}
      />
      <div className="flex flex-col gap-2 pt-6 px-[12px]">
        <div className="flex items-center self-center gap-1 text-attention">
          <AlertCircleIcon />
          <span className="font-unibet font-medium text-[15px] leading-[9px]">
            {translate('PreMatchStats.PredictedLineUps')}
          </span>
        </div>
        <Filter
          variant="outlined"
          options={teamFilterOptions}
          value={teamFilterValue}
          onValueChange={handleTeamFilterChange}
        />
        <FilterChipGroup
          className="mx-[-12px] px-[12px]"
          options={playerStatsFilterOptions}
          value={playerStatsFilterValue}
          platform={platform}
          onValueChange={handlePlayerStatsFilterChange}
        />
      </div>
      <div className="flex flex-col items-center">
        <Lineup
          lines={team.lines}
          jerseySrc={jerseySrc}
          enabledStats={playerStatsFilterValue ?? 'none'}
        />
        <div className="w-full h-[11px] bg-lineup-bottom-gradient sm400:opacity-0" />
        <div className="w-full h-px bg-pmw-lineup-gradient-border sm400:opacity-0" />
      </div>
      <div className="p-2">
        <SubstitutesSection
          activePlayerStatsFilter={playerStatsFilterValue}
          activeTeamFilter={teamFilterValue}
          jerseySrc={jerseySrc}
        />
      </div>
    </div>
  );
}
